import * as React from 'react';

import {
  enumCurrencyCode,
  enumUnit,
  Money,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { Button, NumberField } from '@rematter/ui-web';
import { Maybe, shortenUnit, getWeightValue } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCornerDownRight, LucideTrash2 } from 'lucide-react';

import * as reconciliationState from '../../_state/loadReconciliation.state';
import { LocalDeduction } from '../../_state/loadReconciliation.state';

import { SelectDeductionType } from '~/shared/components/select-fields/SelectDeductionType';

export interface LoadDeductionLineProps {
  deduction: LocalDeduction;
  unit?: Unit;
  unitPrice?: Maybe<Omit<Money, '__typename'>>;
}

export const LoadDeductionLine = ({
  deduction,
  unit,
  unitPrice,
}: LoadDeductionLineProps) => {
  const canEditDeduction = useAtomValue(
    reconciliationState.$canEditReconciliation,
  );
  const updateLoadDeduction = useSetAtom(
    reconciliationState.$updateLoadDeduction,
  );
  const removeLoadDeduction = useSetAtom(
    reconciliationState.$removeLoadDeduction,
  );
  const deductionTypesById = useAtomValue(
    reconciliationState.$deductionTypesById,
  );

  const isWeightDeduction =
    deductionTypesById[deduction.deductionTypeId]?.applicationType === 'Weight';

  const deductionTotal = isWeightDeduction
    ? (deduction.computedAmount ?? 0)
    : (deduction.amount ?? 0);

  return (
    <div className="@LoadDeductionLine grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_50px_50px] gap-2">
      <div className={'flex items-center gap-2'}>
        {deduction.receivedLineItemId && (
          <div className="flex h-full items-center rounded-md bg-failure/10 px-3 text-failure">
            <LucideCornerDownRight size={16} />
          </div>
        )}
        <SelectDeductionType
          value={deduction.deductionTypeId}
          isDisabled={!canEditDeduction}
          className="w-full text-failure"
          onChange={(value) => {
            if (value) {
              updateLoadDeduction({
                deductionId: deduction.id,
                deductionTypeId: value as string,
              });
            }
          }}
        />
      </div>
      <div className="col-span-2" />
      {isWeightDeduction && (
        <NumberField
          autoFocus={canEditDeduction}
          isDisabled={!canEditDeduction}
          prefix={<span className="text-failure/40">W</span>}
          suffix={
            <span className="text-failure/40">
              {shortenUnit(unit ?? enumUnit.Pounds)}
            </span>
          }
          value={getWeightValue(deduction.weight ?? null)}
          className="[&_input]:text-right [&_input]:text-failure"
          formatOptions={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          showSteppers={false}
          onChange={(value) =>
            updateLoadDeduction({
              deductionId: deduction.id,
              weight: value,
            })
          }
        />
      )}
      {!isWeightDeduction && <div />}
      <div />
      <NumberField
        autoFocus={!isWeightDeduction && canEditDeduction}
        className="[&_input]:text-right [&_input]:text-failure"
        value={deductionTotal}
        formatOptions={{
          style: 'currency',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          currency: unitPrice?.currencyCode ?? enumCurrencyCode.USD,
          currencyDisplay: 'narrowSymbol',
        }}
        isDisabled={isWeightDeduction || !canEditDeduction}
        showSteppers={false}
        onChange={(value) =>
          updateLoadDeduction({
            deductionId: deduction.id,
            amount: value,
          })
        }
      />
      <div />
      <Button
        variant="text"
        color="failure"
        onClick={() => removeLoadDeduction({ deductionId: deduction.id })}
        isDisabled={!canEditDeduction}
      >
        <LucideTrash2 size={16} />
      </Button>
    </div>
  );
};
